<template>
  <div>
    <!-- route to popup [[src/components/dialog/creatorProgram/Business.vue]] -->
    <!-- All popups will appear here -->
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame
      :title="$t('creator_program_businesses')"
      :total-count="pagination.totalItems"
    >
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:content>
        <BusinessesFilters @filter="onFilter" @clearFilters="onClearFilters">
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="businessesColumns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </BusinessesFilters>

        <BusinessesTable
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :items="businesses"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          :on-select-item="selectItem"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import businessesColumns from '../../config/businesses/columns';
import {isStr} from '@/utils';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import BusinessesFilters from '../../components/forms/filters/Businesses';
import BusinessesTable from '../../components/table/Businesses';

export default {
  mixins: [urlPaginationRestore],
  components: {
    BusinessesFilters,
    BusinessesTable
  },
  data() {
    return {
      businessesColumns,
      pagination: {
        order: 'desc' ,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'created_at',
        totalItems: 0,
        search: '',
      },
      totalPages: 0,
      query: {
        per_page: 15,
      },
      isLoading: false,
    };
  },
  computed: {
    addAction() {
      return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
    },
    headerActions() {
      return [
        this.$ability.can('store', 'loyalty_program') && {
          key: 'add',
          title: this.$t('add_new_business'),
          onClick: this.openNewBusinessModal,
        },
      ].filter(Boolean);
    },
    businesses() {
      return this.$store.getters['creatorProgram/businesses'];
    },
  },
  methods: {
    // Emitted by the dialog routes after a successful save. Keeps the
    // refetch in this view, with this view's own query, and out of the URL.
    onSaved() {
      this.getItems(this.query);
    },
    getItems(query) {
      this.isLoading = true;
      this.pagination.order = query.order;

      return this.$store
        .dispatch('creatorProgram/getBusinesses', query)
        .then((res) => {
          const { businessesMeta } = res;
          const { total, current_page, last_page } = businessesMeta;

          this.pagination.page = current_page;
          this.pagination.totalItems = total;
          this.totalPages = last_page;
        })
        .catch((err) => {
          return err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    selectItem(business) {
      this.$store.dispatch('creatorProgram/setActiveBusiness', business).then(() => {
        this.$router.push({name: 'creator-program-businesses-details', params: {id: business.id}, query: this.$route.query});
      });
    },
    onUpdatePagination({ page, order, sortBy }) {
      const { query, pagination } = this;
      this.pagination.order = order;
      this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

      const nextQuery = {
        ...query,
        page,
        order,
        sort: isStr(sortBy) ? sortBy : pagination.sortBy,
      };

      this.query = nextQuery;
      this.runInitialFetch(() => this.getItems(nextQuery));
    },
    onChangePage(page) {
      const { query, pagination } = this;
      const { order, sortBy } = pagination;
      const nextQuery = {
        ...query,
        page,
        order,
        sort: sortBy,
      };

      this.query = nextQuery;
      this.getItems(nextQuery);
    },
    onFilter({ search, labels, countries, status, dates }) {
      this.query = {
        ...this.query,
        page: 1,
        search: search || undefined,
        dates: dates || undefined,
        status: status != null ? status : undefined,
        labels,
        countries,
      };

      this.runInitialFetch(() => this.getItems(this.query));
    },
    onClearFilters() {
      const { sortBy, order } = this.pagination;
      this.query = { sort: sortBy, order, per_page: 15, page: 1 };
      this.getItems(this.query);
    },
    openNewBusinessModal() {
      this.$router.push({name: 'creator-program-businesses-new', query: this.$route.query});
    },
  },
};
</script>
